import { useEffect, useState } from "react";
import { useLang } from "@/context/LanguageContext";
import { api, formatApiError } from "@/lib/api";
import BranchNav from "@/components/BranchNav";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Calendar, Clock, MapPin } from "lucide-react";
import { toast } from "sonner";
import { resolveImageUrl } from "@/components/ImageUploader";

function formatDate(iso, lang) {
  if (!iso) return "";
  try {
    const d = new Date(iso + "T00:00:00");
    return d.toLocaleDateString(lang === "pt" ? "pt-PT" : "en-GB", {
      weekday: "long",
      day: "2-digit",
      month: "long",
      year: "numeric",
    });
  } catch {
    return iso;
  }
}

function dateParts(iso) {
  if (!iso) return { d: "", m: "", y: "" };
  const d = new Date(iso + "T00:00:00");
  return {
    d: String(d.getDate()).padStart(2, "0"),
    m: d.toLocaleString("en", { month: "short" }).toUpperCase(),
    y: String(d.getFullYear()),
  };
}

const FALLBACK_GALLERY = [
  "https://images.pexels.com/photos/5779364/pexels-photo-5779364.jpeg?auto=compress&cs=tinysrgb&w=1200",
  "https://images.unsplash.com/photo-1601050690597-df0568f70950?w=1200",
  "https://images.unsplash.com/photo-1626200419199-391ae4be7a41?w=1200",
  "https://images.unsplash.com/photo-1634657443172-efbae44fd04b?w=1200",
  "https://images.unsplash.com/photo-1690983323310-3f8e55c9272f?w=1200",
  "https://images.unsplash.com/photo-1571197119282-7c4e2c2c2c2c?w=1200",
];

export default function Dining() {
  const { tr, lang } = useLang();
  const [events, setEvents] = useState([]);
  const [site, setSite] = useState({});
  const [form, setForm] = useState({ name: "", email: "", phone: "", guests: "2", message: "" });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    api.get("/events", { params: { only_upcoming: true } }).then((r) => setEvents(r.data)).catch(() => {});
    api.get("/site-content").then((r) => setSite(r.data)).catch(() => {});
    document.title = "Lama Hummus — Tábua · Coimbra";
  }, []);

  const upcoming = events[0];
  const gallery = (site.gallery_images && site.gallery_images.length ? site.gallery_images : FALLBACK_GALLERY).slice(0, 6);

  async function submitReservation(e) {
    e.preventDefault();
    setSubmitting(true);
    try {
      // Re-use wholesale-requests for now to capture reservations as inbound leads
      await api.post("/wholesale-requests", {
        business_name: `Reservation — ${form.name}`,
        contact_name: form.name,
        email: form.email,
        phone: form.phone,
        estimated_volume: `Guests: ${form.guests}`,
        message: form.message,
      });
      toast.success(tr("dining.formSent"));
      setForm({ name: "", email: "", phone: "", guests: "2", message: "" });
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Error");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="min-h-screen bg-[#FDFBF7] grain" data-testid="page-dining">
      <BranchNav branch="dining" />

      {/* HERO */}
      <section className="px-6 md:px-10 pt-16 pb-20 max-w-7xl mx-auto" data-testid="dining-hero">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-end">
          <div className="lg:col-span-7 animate-fade-up">
            <div className="text-[11px] uppercase tracking-[0.3em] text-[#8C7A6B] mb-6">
              {tr("dining.heroOverline")}
            </div>
            <h1 className="font-serif text-5xl md:text-7xl font-light leading-[0.95] tracking-tight whitespace-pre-line text-[#1A1A1A]">
              {tr("dining.heroTitle")}
            </h1>
            <p className="mt-8 max-w-xl text-base md:text-lg text-[#1A1A1A]/75 leading-relaxed">
              {tr("dining.heroLead")}
            </p>
          </div>
          <div className="lg:col-span-5">
            <div className="relative overflow-hidden">
              <img
                src="https://images.unsplash.com/photo-1626200419199-391ae4be7a41?auto=format&fit=crop&w=900&q=80"
                alt="Hummus & shawarma"
                className="w-full h-[420px] object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* EVENT CARD */}
      <section id="events" className="px-6 md:px-10 py-20 bg-[#F4F1EA] border-y border-[#E5E0D8]">
        <div className="max-w-7xl mx-auto">
          <div className="text-[11px] uppercase tracking-[0.3em] text-[#8C7A6B] mb-8">
            {tr("dining.heroOverline")}
          </div>

          {upcoming ? (
            <div className="grid grid-cols-12 gap-6 md:gap-10 items-start" data-testid="upcoming-event">
              <div className="col-span-12 md:col-span-4">
                <div className="border border-[#1A1A1A] p-8 md:p-10">
                  <div className="font-serif text-7xl md:text-8xl font-light leading-none text-[#1A1A1A]">
                    {dateParts(upcoming.date).d}
                  </div>
                  <div className="mt-2 text-[11px] uppercase tracking-[0.3em] text-[#1A1A1A]">
                    {dateParts(upcoming.date).m} {dateParts(upcoming.date).y}
                  </div>
                  <div className="mt-6 space-y-2 text-sm text-[#1A1A1A]/80">
                    <div className="flex items-center gap-2"><Clock className="w-3.5 h-3.5" /> {upcoming.time}</div>
                    <div className="flex items-center gap-2"><MapPin className="w-3.5 h-3.5" /> {upcoming.location}</div>
                    {upcoming.address && (
                      <div className="text-xs text-[#1A1A1A]/60 pl-5">{upcoming.address}</div>
                    )}
                  </div>
                </div>
              </div>

              <div className="col-span-12 md:col-span-8">
                <h3 className="font-serif text-3xl md:text-5xl font-light leading-tight text-[#1A1A1A]">
                  {lang === "pt" ? upcoming.title_pt : upcoming.title_en}
                </h3>
                <p className="mt-4 text-base text-[#1A1A1A]/75 max-w-2xl leading-relaxed">
                  {lang === "pt" ? upcoming.description_pt : upcoming.description_en}
                </p>
                <div className="mt-6 text-[11px] uppercase tracking-[0.3em] text-[#8C7A6B]">
                  {formatDate(upcoming.date, lang)}
                </div>

                {/* MENU */}
                <div id="menu" className="mt-12 pt-10 border-t border-[#E5E0D8]">
                  <div className="flex items-baseline justify-between mb-8">
                    <h4 className="font-serif text-2xl md:text-3xl text-[#1A1A1A]">
                      {tr("dining.menuTitle")}
                    </h4>
                    <span className="text-[11px] uppercase tracking-[0.3em] text-[#8C7A6B]">EUR</span>
                  </div>
                  <ul className="space-y-5" data-testid="event-menu">
                    {(upcoming.menu || []).map((m, i) => (
                      <li key={i} className="flex items-baseline">
                        <div className="min-w-0">
                          <div className="font-serif text-xl md:text-2xl text-[#1A1A1A]">
                            {lang === "pt" ? m.name_pt : m.name_en}
                          </div>
                          {(m.description_pt || m.description_en) && (
                            <div className="text-sm text-[#1A1A1A]/60 mt-0.5">
                              {lang === "pt" ? m.description_pt : m.description_en}
                            </div>
                          )}
                        </div>
                        <div className="menu-leader" />
                        <div className="font-mono text-sm md:text-base text-[#1A1A1A] whitespace-nowrap">
                          € {Number(m.price).toFixed(2)}
                        </div>
                      </li>
                    ))}
                  </ul>
                  <p className="mt-8 text-xs text-[#1A1A1A]/50 italic">
                    {tr("dining.menuFootnote")}
                  </p>
                </div>
              </div>
            </div>
          ) : (
            <div className="py-20 text-center text-[#1A1A1A]/60" data-testid="no-event">
              {tr("dining.noEvent")}
            </div>
          )}
        </div>
      </section>

      {/* STORY */}
      <section id="story" className="px-6 md:px-10 py-24 max-w-7xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-5">
            <img
              src="https://images.unsplash.com/photo-1604908176997-125f25cc6f3d?auto=format&fit=crop&w=900&q=80"
              alt="Founders"
              className="w-full h-[500px] object-cover"
            />
          </div>
          <div className="lg:col-span-7">
            <div className="text-[11px] uppercase tracking-[0.3em] text-[#8C7A6B] mb-6">
              {tr("dining.storyOverline")}
            </div>
            <h2 className="font-serif text-4xl md:text-6xl font-light leading-[1.02] tracking-tight whitespace-pre-line text-[#1A1A1A]">
              {tr("dining.storyTitle")}
            </h2>
            <p className="mt-8 text-base md:text-lg text-[#1A1A1A]/75 leading-relaxed max-w-xl">
              {lang === "pt" ? site.story_pt : site.story_en}
            </p>
            <div className="mt-8 text-[11px] uppercase tracking-[0.3em] text-[#1A1A1A]/60">
              Glib · Mark · Tábua, Coimbra
            </div>
          </div>
        </div>
      </section>

      {/* GALLERY */}
      <section id="gallery" className="px-6 md:px-10 py-24 bg-[#1A1A1A] text-[#FDFBF7]">
        <div className="max-w-7xl mx-auto">
          <div className="text-[11px] uppercase tracking-[0.3em] text-[#8C7A6B] mb-4">
            {tr("dining.galleryOverline")}
          </div>
          <h2 className="font-serif text-4xl md:text-6xl font-light tracking-tight mb-12">
            {tr("dining.galleryTitle")}
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4 auto-rows-[180px] md:auto-rows-[220px]" data-testid="gallery">
            {gallery.map((src, i) => {
              const span = [
                "col-span-2 row-span-2",
                "col-span-1 row-span-1",
                "col-span-1 row-span-1",
                "col-span-1 row-span-2",
                "col-span-2 row-span-1",
                "col-span-1 row-span-1",
              ][i % 6];
              return (
                <div key={i} className={`${span} overflow-hidden group`}>
                  <img
                    src={resolveImageUrl(src)}
                    alt=""
                    className="w-full h-full object-cover transition-transform duration-1000 ease-out group-hover:scale-105"
                  />
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* RESERVATION FORM */}
      <section id="reserve" className="px-6 md:px-10 py-24 max-w-3xl mx-auto">
        <div className="text-[11px] uppercase tracking-[0.3em] text-[#8C7A6B] mb-6">
          {tr("dining.cta")}
        </div>
        <h2 className="font-serif text-4xl md:text-5xl font-light tracking-tight text-[#1A1A1A]">
          {tr("dining.reserveTitle")}
        </h2>
        <p className="mt-4 text-[#1A1A1A]/75">{tr("dining.reserveLead")}</p>

        <form onSubmit={submitReservation} className="mt-10 space-y-6" data-testid="reservation-form">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <FieldInline label={tr("dining.formName")} required>
              <Input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="bg-transparent border-0 border-b border-[#E5E0D8] rounded-none focus-visible:ring-0 focus-visible:border-[#1A1A1A] px-0"
                required
                data-testid="resv-name"
              />
            </FieldInline>
            <FieldInline label={tr("dining.formEmail")} required>
              <Input
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                className="bg-transparent border-0 border-b border-[#E5E0D8] rounded-none focus-visible:ring-0 focus-visible:border-[#1A1A1A] px-0"
                required
                data-testid="resv-email"
              />
            </FieldInline>
            <FieldInline label={tr("dining.formPhone")}>
              <Input
                value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
                className="bg-transparent border-0 border-b border-[#E5E0D8] rounded-none focus-visible:ring-0 focus-visible:border-[#1A1A1A] px-0"
                data-testid="resv-phone"
              />
            </FieldInline>
            <FieldInline label={tr("dining.formGuests")}>
              <Input
                type="number"
                min="1"
                value={form.guests}
                onChange={(e) => setForm({ ...form, guests: e.target.value })}
                className="bg-transparent border-0 border-b border-[#E5E0D8] rounded-none focus-visible:ring-0 focus-visible:border-[#1A1A1A] px-0"
                data-testid="resv-guests"
              />
            </FieldInline>
          </div>

          <FieldInline label={tr("dining.formMessage")}>
            <Textarea
              rows={4}
              value={form.message}
              onChange={(e) => setForm({ ...form, message: e.target.value })}
              className="bg-transparent border-0 border-b border-[#E5E0D8] rounded-none focus-visible:ring-0 focus-visible:border-[#1A1A1A] px-0 resize-none"
              data-testid="resv-message"
            />
          </FieldInline>

          <Button
            type="submit"
            disabled={submitting}
            className="bg-[#1A1A1A] text-[#FDFBF7] hover:bg-black rounded-none px-10 py-6 text-[11px] uppercase tracking-[0.3em]"
            data-testid="resv-submit"
          >
            {submitting ? "..." : tr("dining.formSubmit")}
          </Button>
        </form>
      </section>

      <Footer />
    </div>
  );
}

function FieldInline({ label, required, children }) {
  return (
    <div>
      <Label className="text-[11px] uppercase tracking-[0.25em] text-[#8C7A6B]">
        {label}{required && <span className="text-[#8C7A6B]"> *</span>}
      </Label>
      <div className="mt-2">{children}</div>
    </div>
  );
}
